import React from 'react'
import { Redirect } from 'react-router-dom'
import Row from 'muicss/lib/react/row'
import Col from 'muicss/lib/react/col'

// Icons
import ActionDelete from 'material-ui-legacy/svg-icons/action/delete'

// Custom UI components
import errors from '../../lib/errors.js'
import EditButton from '../UI/EditButton'
import CloneButton from '../UI/CloneButton'
import DeleteButton from '../UI/DeleteButton'
import PrintButton from '../UI/PrintButton'
import ErrorDialog from '../UI/ErrorDialog'
import ConfirmationDialog from '../UI/ConfirmationDialog'
import SaveButton from "../UI/SaveButton";
import CancelButton from "../UI/CancelButton";

import AutoCompleteSelect from "../UI/AutoCompleteSelect";
import Dialog from 'material-ui-legacy/Dialog'

import ListActuals from './ListActuals'
import Resource from '../../stores/resource'

const ActualStore = Resource('actuals')
const ProjectStore = Resource('projects')
const QuoteStore = Resource('quotes')

// INTERNAL COMPONENTS ---------------------------------------------------------

/*
Component used to display actual action buttons within details row on DataGrid
Props:
- editCallback,
  printInternalCallback,
  printClientCallback,
  deleteCallback: action callbacks
- readOnly: Boolean(false) self-explanatory
*/
const ActualActionButtons = ({
  editCallback,
  printInternalCallback,
  printClientCallback,
  copyCallback,
  deleteCallback,
  readOnly
}) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <span style={{ marginRight: '10px' }}>Actions:</span>
    {!readOnly && editCallback && <EditButton onTouchTap={editCallback} />}
    {printInternalCallback && (
      <PrintButton
        label="Print accounting version"
        onTouchTap={printInternalCallback}
      />
    )}
    {printClientCallback && (
      <PrintButton label="Print client version" onTouchTap={printClientCallback} />
    )}
    {copyCallback && <CloneButton label="Copy to Another Project as Quote" onTouchTap={copyCallback} />}
    {!readOnly &&
      deleteCallback && <DeleteButton onTouchTap={deleteCallback} />}
  </div>
)

/*
Component used to display details row on DataGrid
Props:
- actualRow: a actual row from ListActuals (NOT a actual object!)

- editCallback,
  cloneCallback,
  printInternalCallback,
  printClientCallback,
  deleteCallback: passed down to ActualActionButtons
- readOnly: Boolean(false) self-explanatory
*/
const ActualDetailsRow = props => (
  <section style={{ display: 'flex', alignItems: 'center' }}>
    <ActualActionButtons {...props} />
  </section>
)

const ActualCopyDialog = ({
  changeCallback,
  copyCallback,
  cancelCallback,
  project,
  open
}) => {

  const actions = [
    <SaveButton
      disabled={ project == null }
      onTouchTap={ () => (project != null) && copyCallback(project) }
      label={'Copy'}
    />,
    <CancelButton
      onTouchTap={cancelCallback}
      label={'Cancel'}
    />
  ]

  return (
    <Dialog
      actions={actions}
      modal={false}
      onRequestClose={cancelCallback}
      open={open}
      //     style={style}
      title={'Select a Project to copy this quote to:'}
    >
      <div class="dialog-body">
        <AutoCompleteSelect
          name="project"
          value={project}
          floatingLabelText="Project name"
          //            selectedId={props.selectedId}
          dataStore={ProjectStore}
          autoCompleteFilter="campaignAutocomplete"
          autoCompleteAttribute='name'
          //            autoCompleteAttribute="project"
          validatedOnChange={changeCallback}
          fullWidth
          required
          openOnFocus={true}
          errorMessage="You must select a project to copy to."
        />
      </div>
    </Dialog>
  )
}

/*
Props:
- deleteCallback: function(), called when delete is confirmed
- cancelCallback: function(), called on cancel
- open: Boolean - show dialog if true
- quote: the quote object to be deleted
*/
const ActualDeleteConfirmation = ({
  deleteCallback,
  cancelCallback,
  open,
  actual
}) => {
  return (
    <ConfirmationDialog
      confirmLabel="Delete"
      confirmIcon={<ActionDelete />}
      confirmCallback={deleteCallback}
      cancelCallback={cancelCallback}
      title="Delete confirmation"
      open={open}
    >
      <p>
        Are you sure you want to delete the actual{' '}
        <strong>{actual.attributes.name}</strong>? There's no undo!
      </p>
    </ConfirmationDialog>
  )
}

/*
MAIN COMPONENT -----------------------------------------------------------------

NOTE: this component is meant to be used from within QuotesPager or similar

Props:
- project: a project object
- actualId: Automatically selects a given actual (optional)
- readOnly: Boolean(false): self-explanatory
- selectCallback(actual): Callback function to be called when
                          a actual is selected
- refreshCallback(): Callback function to be called when the container
                     component needs to be refreshed. This will
                     usually be QuotesPager.
*/
class ActualsManager extends React.PureComponent {
  constructor(props) {
    super(props)

    const { project } = this.props

    this.state = {
      project: project,
      selectedActual: null,
      showDeleteConfirmation: false,
      showCopyDialog: false,
      copyProject: null,
      showCopyAllDialog: false,
      copyAllProject: null,
      error: null,
      redirect: ''
    }
  }

  componentDidMount() {
    const { actualId } = this.props
    if (actualId) this._loadQuote(actualId)
  }

  _refresh = () => {
    const { refreshCallback, selectCallback } = this.props

    this.setState({
      showCopyDialog: false,
      showDeleteConfirmation: false,
      selectedActual: null,
      copyProject: null,
      showCopyAllDialog: false,
      copyAllProject: null,
      error: null
    },
    () => {
      if (selectCallback) selectCallback(null)
      if (refreshCallback) refreshCallback()
    })
  }

  _loadActual = actualId => {
    const { selectCallback } = this.props
    ActualStore.find(actualId)
      .then(result => {
        this.setState({ selectedActual: result.data }, () => {
          if (selectCallback) selectCallback(result.data)
        })
      })
      .catch(error =>
        this.setState({ error: errors.unpack(error), loading: false })
      )
  }

  _projectChangeCopy = (value, valid, attribute) =>
  {
    if (valid && (attribute === 'project'))
    {
      this.setState({ copyProject: value });
    }
  }

  _projectChangeCopyAll = (value, valid, attribute) =>
  {
    if (valid && (attribute === 'project'))
    {
      this.setState({ copyAllProject: value });
    }
  }

  _confirmCopy = row => {
    ActualStore.find(row.id)
      .then(result =>
        this.setState({
          selectedActual: result.data,
          showCopyDialog: true
        })
      ).catch(error =>
        this.setState({ error: errors.unpack(error), loading: false })
      )
  }

  _cancelCopy = () => this.setState({ showCopyDialog: false });

  _confirmCopyAll = () => this.setState({ showCopyAllDialog: true });

  _cancelCopyAll = () => this.setState({ showCopyAllDialog: false });

  _doCopyActual = (id, projectId) =>
  {
    return (
      ActualStore
        .clone(id)
        .then(clonedActual =>
          ActualStore
            .doAction('deactualize', clonedActual.data.id, 'POST')
            .then((result) =>
              QuoteStore
                .doCustomAction(`${result.data.data.id}/relationships/project`, 'PATCH', {data: { type: 'projects', id: projectId } })
                .then(() => this.setState({ redirect: `/projects/${projectId}` }))
                .catch(error => this.setState({ error: errors.unpack(error) }))
            )
            .catch(error => this.setState({ error: errors.unpack(error) }))
        )
        .catch(error => this.setState({ error: errors.unpack(error) }))
    );
  }

  _copyActual = () => {
    const { selectedActual, copyProject } = this.state

    this
      ._doCopyActual(selectedActual.id, copyProject)
      .finally(() => this.setState({ showCopyDialog: false }))
    ;
  }

  _copyAll = () => {
    const { copyAllProject } = this.state;

    const actuals = this.props.project.relationships.actuals.data;

    Promise.all(actuals.map(actual =>
      this._doCopyActual(actual.id, copyAllProject)
    ))
    .finally(() => this.setState({ showCopyAllDialog: false }))
  }

  _confirmDelete = row => {
    ActualStore.find(row.id)
      .then(result =>
        this.setState({
          selectedActual: result.data,
          showDeleteConfirmation: true
        })
      )
      .catch(error =>
        this.setState({ error: errors.unpack(error), loading: false })
      )
  }

  _cancelDelete = () => this.setState({ showDeleteConfirmation: false })

  _deleteActual = () => {
    const { selectedActual } = this.state

    // Take care of race condition
    if (!selectedActual) {
      this.setState({ showDeleteConfirmation: false })
      return
    }

    ActualStore.destroy(selectedActual.id)
      .then(() => this._refresh())
      .catch(error => this.setState({ error: errors.unpack(error) }))
  }

  _printInternalActual = row => {
    const projectId = this.state.project.id
    const actualId = row.id
    this.setState({
      redirect: `/projects/${projectId}/actuals/${actualId}/print/internal`
    })
  }

  _printClientActual = row => {
    const projectId = this.state.project.id
    const actualId = row.id
    this.setState({
      redirect: `/projects/${projectId}/actuals/${actualId}/print/client`
    })
  }

  _editActual = row => {
    const projectId = this.state.project.id
    const actualId = row.id
    this.setState({
      redirect: `/projects/${projectId}/actuals/${actualId}/edit`
    })
  }

  render() {
    const { readOnly } = this.props

    const {
      project,
      selectedActual,
      copyProject,
      showCopyDialog,
      copyAllProject,
      showCopyAllDialog,
      showDeleteConfirmation,
      error,
      redirect
    } = this.state

    // Just skip everything if we're redirecting
    if (redirect) return <Redirect push to={redirect} />

    const projectIsInactive = Boolean(
      project.attributes.state.match(/archived|dead/)
    )

    return (
      <section>
        <Row>
          <Col sm="12">
            <ListActuals
              customSubheader={ <div style={{ float: "left" }}><CloneButton label="Copy All to Another Project as Quotes" onTouchTap={() => this._confirmCopyAll()} /></div> }
              projectId={project.id}
              rowClickFn={this._loadActual}
              rowDetailComponent={({ row }) => (
                <ActualDetailsRow
                  actualRow={row}
                  readOnly={projectIsInactive || readOnly}
                  editCallback={() => this._editActual(row)}
                  copyCallback={() => this._confirmCopy(row)}
                  deleteCallback={() => this._confirmDelete(row)}
                  printInternalCallback={() => this._printInternalActual(row)}
                  printClientCallback={() => this._printClientActual(row)}
                />
              )}
              disableFilters
              disablePager
              disableSearch
              disableVisibility
            />
          </Col>
        </Row>

        {selectedActual && (
          <ActualDeleteConfirmation
            actual={selectedActual}
            open={showDeleteConfirmation}
            deleteCallback={this._deleteActual}
            cancelCallback={this._cancelDelete}
          />
        )}

        {selectedActual && (
          <ActualCopyDialog
            open={showCopyDialog}
            project={copyProject}
            changeCallback={this._projectChangeCopy}
            copyCallback={this._copyActual}
            cancelCallback={this._cancelCopy}
          />
        )}

        <ActualCopyDialog
          open={showCopyAllDialog}
          project={copyAllProject}
          changeCallback={this._projectChangeCopyAll}
          copyCallback={this._copyAll}
          cancelCallback={this._cancelCopyAll}
        />

        <ErrorDialog
          open={Boolean(error)}
          closeCallback={() => this.setState({ error: null })}
        >
          <p>
            <strong>Error:</strong> {error}
          </p>
        </ErrorDialog>
      </section>
    )
  }
}

export default ActualsManager



// WEBPACK FOOTER //
// ./src/components/Quotes/ActualsManager.jsx